/**
 * FilterBar.jsx — Reusable collapsible filter + sort panel.
 * Used by ViewBids (buyer) and ViewRequirements (bidder).
 */

import React, { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  TextField,
  Typography,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

export default function FilterBar({ config, values, onChange, onReset, activeCount }) {
  const [open, setOpen] = useState(false);

  return (
    <Accordion expanded={open} onChange={() => setOpen(!open)} sx={{ mb: 2 }} elevation={1}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FilterListIcon fontSize="small" />
          <Typography variant="subtitle2">Filters &amp; Sort</Typography>
          {activeCount > 0 && (
            <Chip label={`${activeCount} active`} size="small" color="primary" />
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {config.map((field) => {
            if (field.type === 'select') {
              return (
                <FormControl key={field.key} size="small" sx={{ minWidth: 160 }}>
                  <InputLabel>{field.label}</InputLabel>
                  <Select
                    value={values[field.key] || ''}
                    label={field.label}
                    onChange={(e) => onChange(field.key, e.target.value)}
                  >
                    <MenuItem value=""><em>Any</em></MenuItem>
                    {field.options.map((o) => (
                      <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              );
            }
            if (field.type === 'number') {
              return (
                <TextField
                  key={field.key}
                  label={field.label}
                  size="small"
                  type="number"
                  value={values[field.key] || ''}
                  onChange={(e) => onChange(field.key, e.target.value)}
                  sx={{ width: 150 }}
                  inputProps={{ min: field.min, max: field.max, step: field.step || 1 }}
                  placeholder={field.placeholder}
                />
              );
            }
            if (field.type === 'text') {
              return (
                <TextField
                  key={field.key}
                  label={field.label}
                  size="small"
                  value={values[field.key] || ''}
                  onChange={(e) => onChange(field.key, e.target.value)}
                  sx={{ width: 180 }}
                  placeholder={field.placeholder}
                />
              );
            }
            if (field.type === 'range_slider') {
              const val = values[field.key] || [field.min, field.max];
              return (
                <Box key={field.key} sx={{ width: 220, px: 1 }}>
                  <Typography variant="caption" color="text.secondary">{field.label}</Typography>
                  <Slider
                    value={val}
                    min={field.min}
                    max={field.max}
                    step={field.step || 1}
                    onChange={(_, v) => onChange(field.key, v)}
                    valueLabelDisplay="auto"
                    valueLabelFormat={field.format || ((v) => v)}
                    size="small"
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption">{field.format ? field.format(val[0]) : val[0]}</Typography>
                    <Typography variant="caption">{field.format ? field.format(val[1]) : val[1]}</Typography>
                  </Box>
                </Box>
              );
            }
            return null;
          })}
        </Box>

        <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
          <Button size="small" variant="outlined" onClick={onReset}>
            Reset filters
          </Button>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}
